import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatINR } from "@/lib/helpers";
import { X, Lightning, ShieldCheck, CheckCircle } from "@phosphor-icons/react";

export default function BuyCreditsModal({ open, onClose, onPurchased, reason }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buying, setBuying] = useState(null); // pack id being processed

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/credits/packs")
      .then(r => setPacks(r.data.packs || []))
      .catch(() => toast.error("Failed to load credit packs"))
      .finally(() => setLoading(false));
  }, [open]);

  const startPurchase = async (pack) => {
    setBuying(pack.id);
    try {
      const r = await api.post("/billing/credits/order", { pack_id: pack.id });
      // DEMO fallback: credits already granted server-side
      if (r.data.mode === "demo" || !r.data.razorpay_order) {
        toast.success(`${r.data.credits_added || pack.credits} credits added (demo mode)`);
        onPurchased?.(r.data.balance_after);
        return;
      }
      openRazorpay(r.data.razorpay_order, pack);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally {
      setBuying(null);
    }
  };

  const openRazorpay = (order, pack) => {
    if (!window.Razorpay) return toast.error("Razorpay SDK not loaded");
    const rz = new window.Razorpay({
      key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.id,
      name: "EcomKit", description: `${pack.credits} Gap Finder Credits`,
      handler: async (resp) => {
        try {
          const v = await api.post("/billing/credits/verify", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
            pack_id: pack.id,
          });
          if (v.data.duplicate) {
            toast("Payment already processed");
          } else {
            toast.success(`${v.data.credits_added} credits added`);
          }
          onPurchased?.(v.data.balance_after);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Payment verification failed");
        }
      },
      modal: { ondismiss: () => toast("Payment cancelled") },
      theme: { color: "#F97316" },
    });
    rz.open();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="buy-credits-modal"
    >
      <div
        className="bg-background border border-border rounded-xl max-w-2xl w-full p-8 relative shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="buy-credits-close"
          className="absolute top-4 right-4 text-muted-foreground hover:text-navy"
        >
          <X size={18} />
        </button>
        <div className="eyebrow mb-2">Credits</div>
        <h2 className="font-display font-bold text-3xl tracking-tighter text-navy">
          Buy Gap Finder Credits
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {reason || "1 credit = 1 successful Gap Finder run. Failed runs consume 0 credits."}
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {loading && (
            <div className="col-span-full font-mono-data text-sm text-muted-foreground py-6 text-center">
              Loading packs…
            </div>
          )}
          {!loading && packs.length === 0 && (
            <div className="col-span-full text-sm text-muted-foreground py-6 text-center">
              No credit packs configured. Ask your admin.
            </div>
          )}
          {packs.map((p) => (
            <div
              key={p.id}
              data-testid={`credit-pack-${p.id}`}
              className="border border-border rounded-lg p-4 flex flex-col gap-2 hover:border-orange/60 transition-colors"
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-display font-semibold text-navy">{p.label || p.id}</div>
                <div className="font-display font-bold text-2xl text-navy tabular-nums">
                  {formatINR(p.price)}
                </div>
              </div>
              <div className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider">
                {p.credits} credits · ₹{(p.price / p.credits).toFixed(2)} each
              </div>
              <button
                data-testid={`buy-pack-${p.id}`}
                disabled={buying === p.id}
                onClick={() => startPurchase(p)}
                className="btn-primary mt-2 justify-center"
              >
                <Lightning size={13} weight="fill" />
                {buying === p.id ? "Processing…" : "Buy"}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-2 text-[10px] font-mono-data text-muted-foreground uppercase tracking-widest">
          <ShieldCheck size={11} />
          Server-side verification · Ledger-tracked · Refunds via Razorpay
        </div>
      </div>
    </div>
  );
}
